/**
 * Process Audit — /finance/reports/process-audit
 * AI Audit Patrol Phase 1: patrol / dry run / attention / trace
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  BellRing,
  CheckCircle2,
  Eye,
  Play,
  Radar,
  RefreshCw,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface AuditFinding {
  id: string;
  check_id: string;
  check_name: string;
  check_area: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'acknowledged' | 'resolved' | 'accepted';
  subject_type?: string;
  subject_ref?: string;
  finding_detail: string;
  recommended_action?: string;
  why_now?: string;
  next_step?: string;
  detected_at: string;
  period_start?: string;
  period_end?: string;
  run_id: string;
  patrol_run_id?: string | null;
  owner_role?: string | null;
  attention_status?: 'detected' | 'notified' | 'opened';
  attendance_status?: 'unattended' | 'acknowledged' | 'assigned' | 'resolved' | 'accepted' | 'escalated';
  first_notified_at?: string | null;
  first_opened_at?: string | null;
  acknowledged_at?: string | null;
  assigned_to?: string | null;
}

interface RunSummary {
  runId: string;
  period: { from: string; to: string };
  totalFindings: number;
  summary: {
    bySeverity: { critical: number; high: number; medium: number; low: number };
    byArea: Record<string, number>;
  };
  checksRun: number;
  generatedAt: string;
  errors?: string[];
}

interface PatrolRecipient {
  userId: string;
  email: string | null;
  displayName: string | null;
  role: string;
}

interface PatrolResult extends RunSummary {
  dryRun: boolean;
  patrolRunId?: string;
  recipients: PatrolRecipient[];
  notifiedCount?: number;
  findingsPreview?: AuditFinding[];
  notifiedFindingIds?: string[];
}

interface FindingTrace {
  finding: AuditFinding;
  patrolRun?: Record<string, unknown> | null;
  notifications: Array<Record<string, unknown>>;
}

interface AuditEvent {
  id: string;
  entryNumber: string;
  entryType: string;
  action: string;
  status: string;
  amount: number;
  currency: string;
  entryDate: string;
  createdAt: string;
  createdBy: string | null;
  reference: string | null;
  narration: string | null;
  voidReason: string | null;
  reversalReason: string | null;
}

interface ProcessAuditData {
  reportType: string;
  period: { from: string; to: string };
  actionFilter: string;
  summary: { total: number; posted: number; voided: number; reversed: number; manual: number };
  events: AuditEvent[];
}

const ACTION_COLORS: Record<string, string> = {
  POSTED: 'bg-green-50 text-green-700',
  VOIDED: 'bg-red-50 text-red-700',
  REVERSED: 'bg-orange-50 text-orange-700',
  MANUAL_JE: 'bg-blue-50 text-blue-700',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-blue-100 text-blue-700 border-blue-200',
};

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-red-50 text-red-700',
  acknowledged: 'bg-yellow-50 text-yellow-700',
  resolved: 'bg-green-50 text-green-700',
  accepted: 'bg-gray-50 text-gray-600',
};

const ATTENTION_COLORS: Record<string, string> = {
  detected: 'bg-slate-100 text-slate-700',
  notified: 'bg-blue-100 text-blue-700',
  opened: 'bg-emerald-100 text-emerald-700',
};

const ATTENDANCE_COLORS: Record<string, string> = {
  unattended: 'bg-red-100 text-red-700',
  acknowledged: 'bg-amber-100 text-amber-700',
  assigned: 'bg-indigo-100 text-indigo-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  accepted: 'bg-slate-100 text-slate-700',
  escalated: 'bg-fuchsia-100 text-fuchsia-700',
};

const AREA_FILTERS = ['ALL', 'AP', 'AR', 'GL', 'PMT', 'TAX'];
const SEV_FILTERS = ['all', 'critical', 'high', 'medium', 'low'];
const STATUS_FILTERS = ['all', 'open', 'acknowledged', 'resolved', 'accepted'];
const FOCUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'unattended', label: 'Unattended' },
  { id: 'escalated', label: 'Escalated' },
] as const;

function thisMonthRange(): { from: string; to: string } {
  const now = new Date();
  return {
    from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function shortTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-SG', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ProcessAuditPage() {
  const [tab, setTab] = useState<'findings' | 'log'>('findings');
  const [runSummary, setRunSummary] = useState<RunSummary | null>(null);
  const [patrolSummary, setPatrolSummary] = useState<PatrolResult | null>(null);
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [total, setTotal] = useState(0);
  const [logData, setLogData] = useState<ProcessAuditData | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [patrolling, setPatrolling] = useState(false);
  const [testingNotice, setTestingNotice] = useState(false);
  const [from, setFrom] = useState(thisMonthRange().from);
  const [to, setTo] = useState(thisMonthRange().to);
  const [areaFilter, setAreaFilter] = useState('ALL');
  const [sevFilter, setSevFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [focusMode, setFocusMode] = useState<'all' | 'new' | 'unattended' | 'escalated'>('unattended');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [trace, setTrace] = useState<FindingTrace | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const logLoadedRef = useRef(false);

  const loadFindings = useCallback(async (
    f: string,
    t: string,
    area: string,
    sev: string,
    status: string,
    mode: 'all' | 'new' | 'unattended' | 'escalated',
    pg: number,
  ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from: f, to: t, page: String(pg), limit: '50', mode });
      if (area !== 'ALL') params.set('area', area);
      if (sev !== 'all') params.set('severity', sev);
      if (status !== 'all') params.set('status', status);
      const res = await api.get<{ findings: AuditFinding[]; pagination: { total: number } }>(
        `/finance/audit/findings?${params}`,
      );
      setFindings(res?.findings ?? []);
      setTotal(res?.pagination?.total ?? 0);
    } catch {
      setFindings([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLog = useCallback(async (f: string, t: string) => {
    if (logLoadedRef.current) return;
    logLoadedRef.current = true;
    setLoading(true);
    try {
      const res = await api.get<ProcessAuditData>(`/finance/reports/process-audit?from=${f}&to=${t}&action=all`);
      setLogData(res ?? null);
    } catch {
      setLogData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrace = useCallback(async (findingId: string) => {
    setTraceLoading(true);
    try {
      await api.patch(`/finance/audit/findings/${findingId}/opened`, {});
      const res = await api.get<FindingTrace>(`/finance/audit/findings/${findingId}/trace`);
      setTrace(res ?? null);
      setFindings((prev) => prev.map((item) => (
        item.id === findingId
          ? { ...item, attention_status: 'opened', first_opened_at: new Date().toISOString() }
          : item
      )));
    } catch {
      setTrace(null);
    } finally {
      setTraceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFindings(from, to, areaFilter, sevFilter, statusFilter, focusMode, 1);
  }, [loadFindings, from, to, areaFilter, sevFilter, statusFilter, focusMode]);

  useEffect(() => {
    if (tab === 'log') loadLog(from, to);
  }, [tab, loadLog, from, to]);

  const runChecks = async () => {
    setRunning(true);
    try {
      const res = await api.post<RunSummary>(`/finance/audit/run-checks?from=${from}&to=${to}`, {});
      setRunSummary(res ?? null);
      setPatrolSummary(null);
      setPage(1);
      await loadFindings(from, to, areaFilter, sevFilter, statusFilter, focusMode, 1);
    } finally {
      setRunning(false);
    }
  };

  const runPatrol = async (dryRun = false) => {
    setPatrolling(true);
    try {
      const endpoint = dryRun ? '/finance/audit/patrol/dry-run' : '/finance/audit/patrol/run';
      const res = await api.post<PatrolResult>(endpoint, {
        from,
        to,
        label: 'Finance Audit Patrol',
        ownerRole: 'finance_manager',
        notifySeverityThreshold: 'high',
      });
      setPatrolSummary(res ?? null);
      setRunSummary(null);
      if (!dryRun) {
        setPage(1);
        await loadFindings(from, to, areaFilter, sevFilter, statusFilter, focusMode, 1);
      }
    } finally {
      setPatrolling(false);
    }
  };

  const sendTestNotification = async () => {
    setTestingNotice(true);
    try {
      await api.post('/finance/audit/patrol/test-notification', {});
    } finally {
      setTestingNotice(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await api.patch(`/finance/audit/findings/${id}`, { status });
      setFindings((prev) => prev.map((finding) => (
        finding.id === id
          ? {
              ...finding,
              status: status as AuditFinding['status'],
              attendance_status: status === 'resolved'
                ? 'resolved'
                : status === 'accepted'
                  ? 'accepted'
                  : finding.attendance_status,
            }
          : finding
      )));
      if (trace?.finding?.id === id) {
        await loadTrace(id);
      }
    } catch {
      /* ignore */
    }
  };

  const acknowledgeFinding = async (id: string) => {
    try {
      await api.patch(`/finance/audit/findings/${id}/acknowledge`, {});
      setFindings((prev) => prev.map((finding) => (
        finding.id === id
          ? {
              ...finding,
              status: 'acknowledged',
              attention_status: 'opened',
              attendance_status: 'acknowledged',
              acknowledged_at: new Date().toISOString(),
            }
          : finding
      )));
      if (trace?.finding?.id === id) {
        await loadTrace(id);
      }
    } catch {
      /* ignore */
    }
  };

  const filteredFindings = findings.filter((finding) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      finding.check_id.toLowerCase().includes(q)
      || finding.check_name.toLowerCase().includes(q)
      || String(finding.finding_detail ?? '').toLowerCase().includes(q)
      || String(finding.subject_ref ?? '').toLowerCase().includes(q)
      || String(finding.why_now ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-3">
        <ShieldAlert className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold">Process Audit</h1>
          <p className="text-sm text-muted-foreground">AI patrol, finance control checks, and human attendance trace</p>
        </div>
      </div>

      <Card>
        <CardContent className="py-3 flex flex-wrap gap-2 items-center">
          <label className="text-xs text-muted-foreground">From</label>
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); logLoadedRef.current = false; }} className="text-sm border rounded px-2 py-1" />
          <label className="text-xs text-muted-foreground">To</label>
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); logLoadedRef.current = false; }} className="text-sm border rounded px-2 py-1" />
          <Button size="sm" variant="outline" onClick={() => runPatrol(true)} disabled={patrolling}>
            <Sparkles className={`h-3.5 w-3.5 mr-1 ${patrolling ? 'animate-pulse' : ''}`} />
            Dry Run
          </Button>
          <Button size="sm" onClick={() => runPatrol(false)} disabled={patrolling} className="bg-primary">
            <Radar className={`h-3.5 w-3.5 mr-1 ${patrolling ? 'animate-pulse' : ''}`} />
            {patrolling ? 'Patrolling…' : 'Run Patrol'}
          </Button>
          <Button size="sm" variant="outline" onClick={runChecks} disabled={running}>
            <Play className={`h-3.5 w-3.5 mr-1 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Running…' : 'Run Checks'}
          </Button>
          <Button size="sm" variant="ghost" onClick={sendTestNotification} disabled={testingNotice}>
            <BellRing className={`h-3.5 w-3.5 mr-1 ${testingNotice ? 'animate-pulse' : ''}`} />
            Test Notice
          </Button>
        </CardContent>
      </Card>

      {runSummary && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex flex-wrap gap-4 items-center text-sm">
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          <span className="font-medium text-green-800">
            Ran {runSummary.checksRun} checks and stored {runSummary.totalFindings} findings
          </span>
          <span className="text-green-700 text-xs">
            Critical: {runSummary.summary.bySeverity.critical} · High: {runSummary.summary.bySeverity.high} · Medium: {runSummary.summary.bySeverity.medium} · Low: {runSummary.summary.bySeverity.low}
          </span>
          <span className="ml-auto text-xs text-green-600">{shortTime(runSummary.generatedAt)}</span>
        </div>
      )}

      {patrolSummary && (
        <div className={`rounded-lg border px-4 py-3 flex flex-wrap gap-4 items-start text-sm ${
          patrolSummary.dryRun ? 'border-blue-200 bg-blue-50' : 'border-indigo-200 bg-indigo-50'
        }`}>
          <Radar className={`h-4 w-4 shrink-0 ${patrolSummary.dryRun ? 'text-blue-600' : 'text-indigo-600'}`} />
          <div className="min-w-0 flex-1 space-y-1">
            <div className={`font-medium ${patrolSummary.dryRun ? 'text-blue-800' : 'text-indigo-800'}`}>
              {patrolSummary.dryRun ? 'Dry run ready' : 'Patrol completed'}
            </div>
            <div className={`text-xs ${patrolSummary.dryRun ? 'text-blue-700' : 'text-indigo-700'}`}>
              Findings: {patrolSummary.totalFindings} · Critical: {patrolSummary.summary.bySeverity.critical} · High: {patrolSummary.summary.bySeverity.high} · Notified: {patrolSummary.notifiedCount ?? 0}
            </div>
            <div className="flex flex-wrap gap-1 pt-1">
              {patrolSummary.recipients.map((recipient) => (
                <Badge key={`${recipient.userId}-${recipient.role}`} variant="outline" className="text-[10px]">
                  {recipient.role}{recipient.displayName ? ` · ${recipient.displayName}` : ''}
                </Badge>
              ))}
              {patrolSummary.recipients.length === 0 && (
                <Badge variant="outline" className="text-[10px]">No routed recipients</Badge>
              )}
            </div>
          </div>
          <span className={`text-xs ${patrolSummary.dryRun ? 'text-blue-600' : 'text-indigo-600'}`}>{shortTime(patrolSummary.generatedAt)}</span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="py-4 space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">People Got To Know</div>
            <div className="text-2xl font-semibold">{findings.filter((item) => item.attention_status === 'notified' || item.attention_status === 'opened').length}</div>
            <div className="text-xs text-muted-foreground">Notified or opened findings in the current result set</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">People Attended</div>
            <div className="text-2xl font-semibold">{findings.filter((item) => item.attendance_status && item.attendance_status !== 'unattended').length}</div>
            <div className="text-xs text-muted-foreground">Acknowledged, assigned, resolved, or accepted findings</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Still Unattended</div>
            <div className="text-2xl font-semibold">{findings.filter((item) => item.attendance_status === 'unattended').length}</div>
            <div className="text-xs text-muted-foreground">High-signal findings still waiting for a human owner</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-1 border-b">
        {(['findings', 'log'] as const).map((item) => (
          <button
            key={item}
            onClick={() => setTab(item)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === item ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {item === 'findings' ? 'Findings' : 'Activity Log'}
          </button>
        ))}
      </div>

      {tab === 'findings' && (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex gap-1">
              {FOCUS_FILTERS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { setFocusMode(item.id); setPage(1); }}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                    focusMode === item.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              {AREA_FILTERS.map((item) => (
                <button
                  key={item}
                  onClick={() => { setAreaFilter(item); setPage(1); }}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                    areaFilter === item ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              {SEV_FILTERS.map((item) => (
                <button
                  key={item}
                  onClick={() => { setSevFilter(item); setPage(1); }}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors capitalize ${
                    sevFilter === item ? 'bg-muted text-foreground border-border font-semibold' : 'border-border hover:bg-muted'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              {STATUS_FILTERS.map((item) => (
                <button
                  key={item}
                  onClick={() => { setStatusFilter(item); setPage(1); }}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors capitalize ${
                    statusFilter === item ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <Input
              placeholder="Search check, why-now, detail…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="text-sm h-8 w-56 ml-auto"
            />
          </div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground text-sm">Loading findings…</div>
          ) : filteredFindings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              <CheckCircle2 className="h-8 w-8 text-green-400 mx-auto mb-2" />
              No matching findings for this filter set.
              {total === 0 && <p className="mt-1 text-xs">Run Patrol or Run Checks above to scan the selected period.</p>}
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
              <Card>
                <CardContent className="py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Findings</p>
                    <Badge variant="outline" className="text-xs">{total} total</Badge>
                  </div>
                  <div className="space-y-2">
                    {filteredFindings.map((finding) => (
                      <div
                        key={finding.id}
                        className={`rounded-lg border p-3 space-y-2 ${
                          finding.severity === 'critical' ? 'border-red-200 bg-red-50/40'
                          : finding.severity === 'high' ? 'border-orange-200 bg-orange-50/30'
                          : finding.severity === 'medium' ? 'border-yellow-200 bg-yellow-50/20'
                          : 'border-blue-100 bg-blue-50/20'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold border uppercase shrink-0 ${SEVERITY_COLORS[finding.severity]}`}>
                            {finding.severity}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold">
                              <span className="font-mono text-muted-foreground mr-1.5">{finding.check_id}</span>
                              {finding.check_name}
                            </p>
                            {finding.subject_ref && (
                              <p className="text-[11px] text-muted-foreground">{finding.subject_type}: {finding.subject_ref}</p>
                            )}
                          </div>
                          <div className="flex flex-wrap justify-end gap-1.5 shrink-0">
                            <Badge variant="outline" className="text-[10px]">{finding.check_area}</Badge>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[finding.status]}`}>
                              {finding.status}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1">
                          {finding.attention_status && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${ATTENTION_COLORS[finding.attention_status] ?? 'bg-muted text-muted-foreground'}`}>
                              Attention: {finding.attention_status}
                            </span>
                          )}
                          {finding.attendance_status && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${ATTENDANCE_COLORS[finding.attendance_status] ?? 'bg-muted text-muted-foreground'}`}>
                              Attendance: {finding.attendance_status}
                            </span>
                          )}
                          {finding.owner_role && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-700">
                              Owner role: {finding.owner_role}
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-muted-foreground">{finding.finding_detail}</p>
                        {finding.why_now && (
                          <div className="rounded-md bg-white/70 px-2 py-1.5 border border-dashed text-[11px] text-slate-700">
                            <span className="font-semibold">Why now:</span> {finding.why_now}
                          </div>
                        )}
                        {(finding.next_step || finding.recommended_action) && (
                          <div className="rounded-md bg-blue-50/70 px-2 py-1.5 border border-blue-100 text-[11px] text-blue-800">
                            <span className="font-semibold">Next step:</span> {finding.next_step ?? finding.recommended_action}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                          <span>Detected: {shortTime(finding.detected_at)}</span>
                          <span>Notified: {shortTime(finding.first_notified_at)}</span>
                          <span>Opened: {shortTime(finding.first_opened_at)}</span>
                          <span>Acknowledged: {shortTime(finding.acknowledged_at)}</span>
                        </div>

                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                          <button
                            onClick={() => loadTrace(finding.id)}
                            className="text-[10px] px-2 py-0.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                          >
                            <Eye className="inline h-3 w-3 mr-1" />
                            Trace
                          </button>
                          {finding.status === 'open' && (
                            <button
                              onClick={() => acknowledgeFinding(finding.id)}
                              className="text-[10px] px-2 py-0.5 rounded border border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                            >
                              Acknowledge
                            </button>
                          )}
                          {finding.status !== 'resolved' && finding.status !== 'accepted' && (
                            <>
                              <button
                                onClick={() => updateStatus(finding.id, 'resolved')}
                                className="text-[10px] px-2 py-0.5 rounded border border-green-300 text-green-700 hover:bg-green-50"
                              >
                                Resolve
                              </button>
                              <button
                                onClick={() => updateStatus(finding.id, 'accepted')}
                                className="text-[10px] px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                Accept Risk
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {total > 50 && (
                    <div className="flex justify-between items-center mt-4 pt-3 border-t text-xs text-muted-foreground">
                      <span>Page {page} · {total} total</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={page <= 1}
                          onClick={() => {
                            const next = page - 1;
                            setPage(next);
                            loadFindings(from, to, areaFilter, sevFilter, statusFilter, focusMode, next);
                          }}
                        >
                          Prev
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={page * 50 >= total}
                          onClick={() => {
                            const next = page + 1;
                            setPage(next);
                            loadFindings(from, to, areaFilter, sevFilter, statusFilter, focusMode, next);
                          }}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Trace</p>
                    {traceLoading && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
                  </div>
                  {!trace ? (
                    <div className="text-sm text-muted-foreground py-8">
                      Open a finding trace to inspect patrol run, notification history, and attendance state.
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground">Selected finding</div>
                        <div className="font-medium text-sm">{trace.finding.check_id} · {trace.finding.check_name}</div>
                        <div className="text-xs text-muted-foreground">{trace.finding.why_now ?? trace.finding.finding_detail}</div>
                      </div>

                      <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
                        <div className="text-xs font-semibold">Patrol Run</div>
                        <div className="text-xs text-muted-foreground">Patrol ID: {String(trace.patrolRun?.id ?? trace.finding.patrol_run_id ?? '—')}</div>
                        <div className="text-xs text-muted-foreground">Status: {String(trace.patrolRun?.status ?? '—')}</div>
                        <div className="text-xs text-muted-foreground">Started: {shortTime(String(trace.patrolRun?.started_at ?? ''))}</div>
                        <div className="text-xs text-muted-foreground">Completed: {shortTime(String(trace.patrolRun?.completed_at ?? ''))}</div>
                      </div>

                      <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                        <div className="text-xs font-semibold">Notification History</div>
                        {trace.notifications.length === 0 ? (
                          <div className="text-xs text-muted-foreground">No notifications recorded for this finding.</div>
                        ) : (
                          trace.notifications.slice(0, 6).map((item) => (
                            <div key={String(item.id)} className="text-xs border-l-2 border-primary/40 pl-2">
                              <div className="font-medium">{String(item.title ?? 'Notification')}</div>
                              <div className="text-muted-foreground">{shortTime(String(item.created_at ?? ''))}</div>
                              <div className="text-muted-foreground">{String(item.body ?? '')}</div>
                            </div>
                          ))
                        )}
                      </div>

                      <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
                        <div className="text-xs font-semibold">Attendance State</div>
                        <div className="text-xs text-muted-foreground">Attention: {trace.finding.attention_status ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">Attendance: {trace.finding.attendance_status ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">Assigned to: {trace.finding.assigned_to ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">Acknowledged at: {shortTime(trace.finding.acknowledged_at)}</div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {tab === 'log' && (
        loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading activity log…</div>
        ) : !logData ? (
          <div className="text-center py-12 text-muted-foreground text-sm">No activity log data.</div>
        ) : (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold">Activity Log</p>
                <Badge variant="outline" className="text-xs">{logData.events.length} events</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pr-3">Date</TableHead>
                      <TableHead className="pr-3">Entry #</TableHead>
                      <TableHead className="pr-3">Type</TableHead>
                      <TableHead className="pr-3">Action</TableHead>
                      <TableHead className="text-right pr-3">Amount</TableHead>
                      <TableHead className="pr-3">User</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logData.events.map((event) => (
                      <TableRow key={String(event.id)} className="border-b last:border-0 hover:bg-muted/30">
                        <TableCell className="pr-3 tabular-nums">
                          {event.entryDate
                            ? new Date(String(event.entryDate)).toLocaleDateString('en-SG')
                            : new Date(String(event.createdAt)).toLocaleDateString('en-SG')}
                        </TableCell>
                        <TableCell className="pr-3 font-mono">{String(event.entryNumber ?? event.id ?? '')}</TableCell>
                        <TableCell className="pr-3 font-mono">{String(event.entryType ?? '')}</TableCell>
                        <TableCell className="pr-3">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${ACTION_COLORS[event.action] ?? 'bg-muted text-muted-foreground'}`}>
                            {event.action}
                          </span>
                        </TableCell>
                        <TableCell className="text-right pr-3 tabular-nums">{fmt(event.amount)}</TableCell>
                        <TableCell className="pr-3 text-muted-foreground">{String(event.createdBy ?? '—')}</TableCell>
                        <TableCell className="text-muted-foreground max-w-[200px] truncate">
                          {event.voidReason ?? event.reversalReason ?? event.narration ?? event.reference ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}
